import { Navigate, NavLink, Outlet, useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, authClient } from "../api.ts";
import { useBrand, useMe, usePublicConfig, useSettings } from "../hooks.ts";
import { useSaveSettings } from "../pages/Settings.tsx";
import { browserZone } from "../lib/cron.ts";
import { ADMIN_GROUPS } from "../lib/admin-nav.ts";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { usePendingApprovals } from "../pages/Approvals.tsx";
import { cx, Spinner } from "./ui.tsx";
import { Icon, type IconName } from "./icons.tsx";
import { ImpersonationBanner } from "./impersonation.tsx";

/** Daily work first, then what the assistant knows, then what it can reach. Settings sit by your name. */
type NavItem = { to: string; label: string; icon: IconName };
const ADMIN_ICONS: Record<string, IconName> = { people: "people", knowledge: "knowledge", assistants: "assistants", workspace: "workspace" };
const nav: { user: { label: string; items: NavItem[] }[]; admin: NavItem[] } = {
  user: [
    {
      label: "Work",
      items: [
        { to: "/chat", label: "Chat", icon: "chat" },
        { to: "/approvals", label: "Approvals", icon: "approvals" },
        { to: "/dashboard", label: "Dashboard", icon: "dashboard" },
        { to: "/goals", label: "Goals", icon: "goals" },
        { to: "/schedules", label: "Schedules & briefings", icon: "schedules" },
        { to: "/files", label: "Files", icon: "files" },
        { to: "/web-apps", label: "Web apps", icon: "webApps" },
      ],
    },
    {
      label: "Knowledge",
      items: [
        { to: "/memory", label: "Memory", icon: "memory" },
        { to: "/skills", label: "Skills", icon: "skills" },
      ],
    },
    { label: "Connections", items: [{ to: "/connections", label: "Connected apps", icon: "connections" }] },
  ],
  admin: ADMIN_GROUPS.map((g) => ({ to: `/admin/${g.base}`, label: g.label, icon: ADMIN_ICONS[g.base] ?? "settings" })),
};

const sectionLabel = "px-3 pt-5 pb-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-white/35";

/** The logo sits on the dark sidebar colour: Mnemio's white logo, the install's own, or its name. */
export function Logo({ className = "h-6" }: { className?: string }) {
  const b = useBrand();
  if (b.loading) return <span className={cx("inline-block", className)} />;
  if (b.logoUrl) return <img src={b.logoUrl} alt={b.name} className={cx("w-auto max-w-full select-none object-contain", className)} draggable={false} />;
  if (b.custom) return <span className={cx("flex items-center text-lg font-semibold tracking-tight text-white", className)}>{b.name}</span>;
  return <img src="/mnemio-logo.svg" alt={b.name} className={cx("w-auto select-none", className)} draggable={false} />;
}

/** Keeps the tab title, icon and colours in step with Admin → Branding (also after a save). */
export function BrandEffects() {
  const b = useBrand();
  useEffect(() => {
    if (b.loading) return;
    document.title = b.name;
    const root = document.documentElement.style;
    root.setProperty("--color-accent", b.accentColor);
    root.setProperty("--chrome", b.sidebarColor);
    root.setProperty("--chrome-2", `color-mix(in srgb, ${b.sidebarColor} 88%, white)`);
    for (const rel of ["icon", "apple-touch-icon"]) {
      const link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (link) {
        link.removeAttribute("type");
        link.href = b.faviconUrl;
      }
    }
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", b.sidebarColor);
  }, [b.loading, b.name, b.accentColor, b.sidebarColor, b.faviconUrl]);
  return null;
}

/**
 * Until someone picks a time zone, their assistant takes this browser's (once, on their own sign-in —
 * never while an admin is viewing as them). They can change it in Settings.
 */
function useAdoptBrowserZone(enabled: boolean) {
  const settings = useSettings();
  const save = useSaveSettings();
  const tried = useRef(false);
  useEffect(() => {
    if (!enabled || tried.current || !settings.data || settings.data.timezone !== null) return;
    tried.current = true;
    save.mutate({ timezone: browserZone() });
  }, [enabled, settings.data, save]);
}

export function Shell() {
  const me = useMe();
  const cfg = usePublicConfig();
  useAdoptBrowserZone(!!me.data && !me.data.user.impersonatedBy);
  const approvals = usePendingApprovals();
  const qc = useQueryClient();
  const loc = useLocation();
  // Phones and tablets: the menu opens over the page from the top bar, and closes on the next page.
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [loc.pathname]);
  if (me.isPending) return <div className="grid h-full place-items-center"><Spinner /></div>;
  // Remember where they were going (e.g. a /link/… page from a chat app) and return after sign-in.
  if (me.error instanceof ApiError && me.error.status === 401)
    return <Navigate to={loc.pathname === "/" ? "/login" : `/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (me.error) return <div className="grid h-full place-items-center text-sm text-[var(--muted)]">{me.error.message}</div>;
  const { user } = me.data;
  const imp = user.impersonatedBy;
  // Admin pages are the admin's own, never while acting as someone (even another admin): the server refuses them too.
  const admin = user.role === "admin" && !imp;
  if (loc.pathname.startsWith("/admin") && !admin) return <Navigate to="/" replace />;
  // Web apps: when the install has an apps domain and the group may publish them (they're built in the terminal).
  const perms = me.data.group?.permissions;
  const webApps = !!cfg.data?.appsDomain && perms?.webApps !== false && perms?.terminal !== false;
  // Chat is a full-height app surface; every other page is a padded, centred document.
  const fullBleed = loc.pathname.startsWith("/chat");
  const pending = approvals.data?.pending ?? 0;

  const link = (i: NavItem) => (
    <NavLink
      key={i.to}
      to={i.to}
      className={({ isActive }) =>
        cx(
          "group relative flex items-center gap-3 whitespace-nowrap rounded-lg px-3 py-1.5 text-[13.5px] transition-colors",
          isActive
            ? "bg-white/[0.08] font-medium text-white before:absolute before:top-1/2 before:-left-1 before:h-4 before:w-[3px] before:-translate-y-1/2 before:rounded-r-full before:bg-[var(--color-brand)]"
            : "text-[var(--chrome-text)] hover:bg-white/[0.04] hover:text-white",
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon name={i.icon} className={cx("shrink-0 transition-colors", isActive ? "text-[var(--color-brand)]" : "text-white/45 group-hover:text-white/80")} />
          <span className="truncate">{i.label}</span>
          {i.to === "/approvals" && pending > 0 && <CountPill n={pending} />}
        </>
      )}
    </NavLink>
  );

  const menu = (
    <>
      <nav className="-mx-1 flex-1 overflow-y-auto px-1 pb-4">
        {nav.user.map((s) => (
          <div key={s.label} className="space-y-0.5">
            <div className={sectionLabel}>{s.label}</div>
            {s.items.filter((i) => i.to !== "/web-apps" || webApps).map(link)}
          </div>
        ))}
        {admin && (
          <div className="space-y-0.5">
            <div className={sectionLabel}>Admin</div>
            {nav.admin.map(link)}
          </div>
        )}
      </nav>
      <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] p-2.5">
        {user.image ? <img src={user.image} alt="" className="h-8 w-8 rounded-full" /> : <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-white/20 to-white/5 text-xs font-semibold text-white">{(user.name || user.email)[0]?.toUpperCase()}</div>}
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium text-white">{user.name}</div>
          <div className="truncate text-xs text-[var(--chrome-text)]">{user.email}</div>
        </div>
        <NavLink
          to="/settings"
          title="Settings: time zone and signed-in devices"
          aria-label="Settings"
          className={({ isActive }) => cx("rounded-md p-1.5 transition-colors hover:bg-white/10 hover:text-white", isActive ? "bg-white/10 text-white" : "text-[var(--chrome-text)]")}
        >
          <Icon name="settings" />
        </NavLink>
        <button
          title="Sign out"
          aria-label="Sign out"
          className="rounded-md p-1.5 text-[var(--chrome-text)] transition-colors hover:bg-white/10 hover:text-white"
          onClick={async () => {
            await authClient.signOut();
            qc.clear();
            location.href = "/login";
          }}
        >
          <Icon name="signOut" />
        </button>
      </div>
    </>
  );

  return (
    <>
    {imp && <ImpersonationBanner asUser={{ name: user.name, email: user.email }} mode={imp.mode} adminEmail={imp.email} expiresAt={imp.expiresAt} />}
    <div className={cx("flex flex-col lg:flex-row", fullBleed ? (imp ? "h-[calc(100dvh-2.5rem)]" : "h-dvh") : "min-h-full")}>
      {/* Phones and tablets: a slim bar with the logo; the menu slides in over the page. */}
      <header className={cx("chrome-glow sticky z-30 flex h-14 shrink-0 items-center gap-3 bg-[var(--chrome)] px-4 lg:hidden", imp ? "top-10" : "top-0")}>
        <NavLink to="/chat" className="min-w-0"><Logo className="h-5" /></NavLink>
        <div className="ml-auto flex items-center gap-1">
          {pending > 0 && (
            <NavLink to="/approvals" className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs text-[var(--chrome-text)] hover:text-white" aria-label={`${pending} waiting for approval`}>
              <Icon name="approvals" /> <CountPill n={pending} />
            </NavLink>
          )}
          <button aria-label="Open menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(true)} className="rounded-lg p-2 text-white/80 hover:bg-white/10 hover:text-white">
            <Icon name="menu" size={20} />
          </button>
        </div>
      </header>
      <Drawer open={menuOpen} onClose={() => setMenuOpen(false)}>{menu}</Drawer>

      <aside className={cx("chrome-glow hidden w-64 shrink-0 flex-col bg-[var(--chrome)] px-4 pt-5 pb-4 lg:sticky lg:flex", imp ? "lg:top-10 lg:h-[calc(100vh-2.5rem)]" : "lg:top-0 lg:h-screen")}>
        <div className="mb-1 px-3"><Logo /></div>
        {menu}
      </aside>
      {fullBleed ? (
        <main className="min-h-0 min-w-0 flex-1"><Outlet /></main>
      ) : (
        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-12 lg:py-11">
          <div className="mx-auto max-w-5xl"><Outlet /></div>
        </main>
      )}
    </div>
    </>
  );
}

function CountPill({ n }: { n: number }) {
  return <span className="ml-auto inline-grid h-[18px] min-w-[18px] place-items-center rounded-full bg-[var(--color-brand)] px-1.5 text-[10.5px] font-semibold text-[#0c2533]">{n}</span>;
}

/** The phone/tablet menu: the sidebar's contents in a panel over the page. Esc or a tap outside closes it. */
function Drawer({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", esc);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);
  return (
    <div className={cx("fixed inset-0 z-50 lg:hidden", !open && "pointer-events-none")} aria-hidden={!open}>
      <div onClick={onClose} className={cx("absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity duration-200", open ? "opacity-100" : "opacity-0")} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        className={cx("chrome-glow absolute inset-y-0 left-0 flex w-[min(18rem,85vw)] flex-col bg-[var(--chrome)] px-4 pt-4 pb-4 transition-transform duration-200 ease-out", open ? "translate-x-0 shadow-2xl" : "-translate-x-full")}
      >
        <div className="mb-1 flex items-center justify-between pl-3">
          <Logo className="h-5" />
          <button aria-label="Close menu" onClick={onClose} className="rounded-lg p-2 text-white/70 hover:bg-white/10 hover:text-white"><Icon name="close" size={18} /></button>
        </div>
        {open && children}
      </div>
    </div>
  );
}
